import { PlaywrightChronologySchema, PlaywrightReviewSchema, ResolvedFailureEvidenceSchema } from './report-schema.ts'
import { z } from 'zod'
import { isUnusableMcpResult } from '../lib/mcp-error-result.ts'

export const FailureInputSchema = z.object({
  recording_id: z.string().uuid(),
  ci_run_url: z.string().url().max(2000),
  commit_sha: z.string().regex(/^[a-f0-9]{40}$/),
  test_id: z.string().min(1).max(1000),
  title: z.string().min(1).max(2000),
  retry: z.number().int().min(0),
  error: z.string().max(30000),
  source: z.string().max(100000),
  mocked_services: z.boolean(),
}).strict()

export const FailureReportSchema = z.object({
  classification: z.enum(['application', 'test', 'environment', 'unresolved']),
  explanation: z.string().min(1).max(20000),
  evidence: z.array(z.object({ call: z.number().int().positive(), finding: z.string().min(1).max(5000) })).max(30),
  root_cause: z.string().min(1).max(10000).nullable(),
  next_step: z.string().min(1).max(5000),
  limitations: z.string().min(1).max(10000),
  title: z.string().min(1).max(500).optional(),
  chronology: z.array(PlaywrightChronologySchema).max(15).optional(),
  resolved_evidence: z.array(ResolvedFailureEvidenceSchema).max(60).optional(),
  review: PlaywrightReviewSchema.optional(),
}).strict()

export const FailureStateSchema = z.object({
  analysis_id: z.string().min(1),
  input: FailureInputSchema,
  report: FailureReportSchema.optional(),
}).strict()

export function validateFailureEvidence(report: z.infer<typeof FailureReportSchema>, results: string[]) {
  const calls = [...report.evidence.map(item => item.call), ...(report.chronology ?? []).flatMap(step => step.evidence_calls)]
  if (calls.some(call => isUnusableMcpResult(results[call - 1]))) throw new Error('Cite successful evidence calls from this investigation')
  if ((report.classification === 'unresolved') !== (report.root_cause === null)) throw new Error('Only an established cause may be classified as resolved')
  if (report.classification !== 'unresolved' && !report.evidence.length) throw new Error('A resolved explanation requires recording evidence')
}
